/** Frame-time ring buffers and the snapshot shown by the overlay, the bench, and the e2e. */

export interface FrameSample {
  frameMs: number;
  cpuMs: number;
  gpuMs: number | null;
  drawCalls: number;
  triangles: number;
  renderScale: number;
}

export interface SeriesStats {
  avg: number;
  p50: number;
  p95: number;
  p99: number;
  max: number;
}

export interface StatsSnapshot {
  frames: number;
  fps: number;
  frame: SeriesStats;
  cpu: SeriesStats;
  gpu: SeriesStats | null;
  drawCalls: number;
  triangles: number;
  renderScale: { current: number; min: number; avg: number };
  signal: 'gpu' | 'cpu';
}

export function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1));
  return sorted[idx] ?? 0;
}

export function seriesStats(values: readonly number[]): SeriesStats {
  if (values.length === 0) return { avg: 0, p50: 0, p95: 0, p99: 0, max: 0 };
  const sorted = [...values].sort((a, b) => a - b);
  const avg = values.reduce((s, v) => s + v, 0) / values.length;
  return {
    avg,
    p50: percentile(sorted, 50),
    p95: percentile(sorted, 95),
    p99: percentile(sorted, 99),
    max: sorted[sorted.length - 1] ?? 0,
  };
}

export class FrameStats {
  private samples: FrameSample[] = [];
  private total = 0;

  constructor(private readonly capacity = 240) {}

  push(sample: FrameSample): void {
    this.samples.push(sample);
    this.total++;
    if (this.samples.length > this.capacity) this.samples.shift();
  }

  reset(): void {
    this.samples = [];
    this.total = 0;
  }

  get frames(): number {
    return this.total;
  }

  snapshot(): StatsSnapshot {
    const s = this.samples;
    const frame = seriesStats(s.map((x) => x.frameMs));
    const gpuValues = s.map((x) => x.gpuMs).filter((v): v is number => v !== null);
    const gpu =
      gpuValues.length >= Math.min(10, s.length) && s.length > 0 ? seriesStats(gpuValues) : null;
    const last = s[s.length - 1];
    const scales = s.map((x) => x.renderScale);
    return {
      frames: this.total,
      fps: frame.avg > 0 ? 1000 / frame.avg : 0,
      frame,
      cpu: seriesStats(s.map((x) => x.cpuMs)),
      gpu,
      drawCalls: last?.drawCalls ?? 0,
      triangles: last?.triangles ?? 0,
      renderScale: {
        current: last?.renderScale ?? 1,
        min: scales.length ? Math.min(...scales) : 1,
        avg: scales.length ? scales.reduce((a, b) => a + b, 0) / scales.length : 1,
      },
      signal: gpu ? 'gpu' : 'cpu',
    };
  }
}
